import { useState, useCallback } from "react";
import axios from "axios";

interface ApiErrorHandlingOptions {
  defaultErrorMessage?: string;
}

/**
 * Standard API error structure from server
 */
interface ApiError {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  timestamp?: string;
  [key: string]: unknown;
}

interface ApiErrorHandlingReturn {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] }; // Used only for client-side validation
  setApiErrors: (errors: string[]) => void;
  setFieldErrors: (errors: { [k: string]: string[] }) => void;
  clearErrors: () => void;
  handleApiError: (error: unknown) => void;
}

/**
 * Hook for handling API errors with automatic message extraction
 *
 * Server errors are extracted from the new API error structure:
 * - Priority: detail > title > default message
 * - Field errors are only used for client-side validation
 *
 * @param options - Configuration options
 * @returns Error handling utilities
 */
export const useApiErrorHandling = (
  options: ApiErrorHandlingOptions = {}
): ApiErrorHandlingReturn => {
  const { defaultErrorMessage = "An error occurred. Please try again." } =
    options;

  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string[] }>({});

  const clearErrors = useCallback(() => {
    setApiErrors([]);
    setFieldErrors({});
  }, []);

  const handleApiError = useCallback(
    (error: unknown) => {
      console.log("API Error:", error);

      // Check if it's already an ApiError object (from unwrap())
      if (
        error &&
        typeof error === "object" &&
        ("detail" in error || "title" in error)
      ) {
        const data = error as ApiError;
        // Priority: detail > title > default message
        const message = data.detail || data.title || defaultErrorMessage;
        setApiErrors([message]);
        return;
      }

      // Extract error message from axios error response
      if (axios.isAxiosError(error) && error.response?.data) {
        const data = error.response.data as ApiError;
        // Priority: detail > title > default message
        const message = data.detail || data.title || defaultErrorMessage;
        setApiErrors([message]);
        return;
      }

      // Handle plain objects with message property
      if (error && typeof error === "object" && "message" in error) {
        setApiErrors([String(error.message)]);
        return;
      }

      // Fallback to default error message
      setApiErrors([defaultErrorMessage]);
    },
    [defaultErrorMessage]
  );

  return {
    apiErrors,
    fieldErrors, // Kept for client-side validation compatibility
    setApiErrors,
    setFieldErrors,
    clearErrors,
    handleApiError,
  };
};
